/**
 * The chart builder's frame, seen from the page that hosts it.
 *
 * Inside the frame (`embed.ts`) the app installs `window.__ndeBuilder`; the
 * host reads it directly - same origin - for the recipe to save, whether it
 * can be saved, what the pins would freeze, and to take the Catalog
 * thumbnail, which has to come from the frame's own canvas. Whenever any of
 * that may have changed the frame posts `nde-builder-change`, so the host's
 * header re-renders.
 */

import {
  activeDataSource,
  activePreset,
  comparisonShips,
  endDate,
  plottedShipIds,
  selectedShip,
  startDate,
} from '../../state/store.ts';
import { isPlotEnabled, plotState } from '../../state/plot-state.ts';
import { buildQueryString } from '../../state/url-sync.ts';
import { plotFetching } from '../plot/plot-ui-state.ts';
import { captureAndStore } from '../catalog/thumbnail.ts';
import { registerOverlayProvider, unregisterOverlayProvider, type OverlayProvider } from '../../state/chart-overlays.ts';
import { runtime } from '../plot/plot-runtime.ts';

export const BUILDER_CHANGE = 'nde-builder-change';

export interface BuilderFrameApi {
  /** The recipe on screen: the query string a tile or Catalog chart stores. */
  query(): string;
  /** A ship and at least one series: something to save. */
  canSave(): boolean;
  /** What "Pin ships" and "Pin time range" would freeze. */
  ships(): string[];
  preset(): string;
  /** Store the chart on screen as a Catalog chart's thumbnail. */
  capture(chartId: string, version: number): Promise<boolean>;
  /**
   * Shapes the host draws on the frame's chart, through the overlay registry,
   * or null to stop. Argus uses it to preview a rule's episodes and numbers.
   */
  overlay(shapes: OverlayProvider['shapes'] | null): void;
}

const HOST_OVERLAY = 'builder-host';

declare global {
  interface Window {
    __ndeBuilder?: BuilderFrameApi;
  }
}

/** Inside the frame: publish the API and announce changes to the host. */
export function installBuilderFrameApi(): void {
  window.__ndeBuilder = {
    query: () => buildQueryString(),
    canSave: () => isPlotEnabled(),
    ships: () => plottedShipIds(),
    preset: () => activePreset.value,
    capture: (chartId, version) => captureAndStore(chartId, version),
    overlay: (shapes) => {
      if (shapes) registerOverlayProvider({ id: HOST_OVERLAY, shapes });
      else unregisterOverlayProvider(HOST_OVERLAY);
      runtime.instance?.redraw();
    },
  };
  let queued = false;
  const notify = (): void => {
    // One message per burst: a restore writes a dozen atoms in a row.
    if (queued) return;
    queued = true;
    queueMicrotask(() => {
      queued = false;
      window.parent?.postMessage({ type: BUILDER_CHANGE }, window.location.origin);
    });
  };
  for (const atom of [plotState, selectedShip, comparisonShips, activePreset, startDate, endDate, activeDataSource, plotFetching]) {
    (atom as { subscribe(fn: () => void): () => void }).subscribe(notify);
  }
  notify();
}
